import type { TemplateDefinition } from "./types";

export const T13: TemplateDefinition = {
  id: "t13",
  module: 4,
  number: 13,
  name: "Patrol Plan and Situational Awareness Protocol",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Reactive patrol is instinct. Strategic patrol is a certified professional skill. This tool structures every shift for maximum safety and effectiveness.",
  architectureContext:
    "This template serves the Tiered Access pillar. It converts patrol from reactive coverage into a structured, intelligence-driven operation with pre-shift planning and post-shift handoff.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Pre-Shift Intelligence Review",
      fields: [
        { id: "data_sources", type: "textarea", label: "Data sources I review before every shift", placeholder: "Crime stats, BOLOs, community intelligence, prior shift briefing, CAD hot spots, other" },
        { id: "review_routine", type: "textarea", label: "My pre-shift review routine and time required", placeholder: "Your routine and time..." },
        { id: "doc_intel", type: "textarea", label: "How I document my pre-shift intelligence picture", placeholder: "Your documentation approach..." },
      ],
    },
    {
      id: "s2",
      title: "Section 2 — Priority Target Areas",
      repeatable: {
        id: "priority_areas",
        label: "Priority Area",
        maxRows: 5,
        initialRows: 1,
        addLabel: "+ Add Priority Area",
        fields: [
          { id: "area", type: "text", label: "Area Name/Location", placeholder: "Location..." },
          { id: "basis", type: "select", label: "Priority basis", options: [{ label: "Crime data", value: "crime_data" }, { label: "Community complaint", value: "community" }, { label: "BOLO", value: "bolo" }, { label: "Intelligence", value: "intel" }, { label: "Historical pattern", value: "historical" }] },
          { id: "high_risk_time", type: "text", label: "Time of highest risk", placeholder: "e.g., 2200-0200" },
          { id: "approach", type: "textarea", label: "Planned patrol approach", placeholder: "Your patrol approach..." },
          { id: "shifts", type: "text", label: "Applicable shifts (Day/Evening/Overnight)", placeholder: "e.g., Day, Evening" },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Situational Awareness Protocol",
      description: "4-step continuous loop:",
      variant: "steps",
      fields: [
        { id: "baseline", type: "textarea", label: "BASELINE: How I establish the environmental baseline in my district", placeholder: "Your baseline establishment approach..." },
        { id: "anomaly", type: "textarea", label: "ANOMALY RECOGNITION: The specific indicators that tell me something has changed from baseline", placeholder: "Anomaly indicators..." },
        { id: "threat", type: "textarea", label: "THREAT ASSESSMENT: How I assess the threat level of an identified anomaly", placeholder: "Your threat assessment approach..." },
        { id: "decide", type: "textarea", label: "DECIDE AND ACT: My decision protocol for responding to identified threats", placeholder: "Your decision protocol..." },
      ],
    },
    {
      id: "s4",
      title: "Section 4 — End-of-Shift Assessment and Handoff",
      fields: [
        { id: "end_assess", type: "textarea", label: "What I assess at end of shift", placeholder: "Your end-of-shift assessment..." },
        { id: "intel_handoff", type: "textarea", label: "Intelligence I document for the oncoming shift", placeholder: "What you document for handoff..." },
        { id: "briefing", type: "textarea", label: "How I brief the oncoming officer or submit my end-of-shift summary", placeholder: "Your briefing approach..." },
      ],
    },
  ],
};

export const T14: TemplateDefinition = {
  id: "t14",
  module: 4,
  number: 14,
  name: "Crime Scene Management Checklist and Evidence Protocol",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "A broken chain of custody can suppress evidence and dismiss a case. This checklist eliminates the errors that create those breaks.",
  architectureContext:
    "This template serves the Tiered Access pillar. It converts crime scene management into a pre-flight checklist system — ensuring scene integrity, evidence preservation, and chain of custody from arrival through release.",
  sections: [
    {
      id: "s1",
      title: "Section 1 — Scene Arrival and Control",
      checklist: {
        id: "arrival_control",
        label: "Scene Arrival and Control",
        items: [
          { id: "safety_assess", label: "Scene safety assessment completed before entry — threats cleared" },
          { id: "perimeter", label: "Perimeter established — inner and outer perimeter defined" },
          { id: "boundaries", label: "Scene boundaries communicated to all responding officers" },
          { id: "witnesses_id", label: "Witnesses identified and separated" },
          { id: "witnesses_doc", label: "Witness identification documented (names, contact info, location at time of incident)" },
          { id: "media_exclusion", label: "Media and public exclusion from scene enforced" },
          { id: "scene_log", label: "Scene log initiated — all personnel entering/exiting logged" },
        ],
      },
    },
    {
      id: "s2",
      title: "Section 2 — Evidence Identification",
      checklist: {
        id: "evidence_id",
        label: "Evidence Identification",
        items: [
          { id: "photographed", label: "Full scene photographed before touching anything — overview, mid-range, close-up" },
          { id: "video", label: "Video walkthrough recorded if available" },
          { id: "sketch", label: "Sketch of scene completed with measurements" },
          { id: "flagged", label: "All potential evidence identified and flagged before collection" },
          { id: "evidence_log", label: "Evidence log initiated with item number, description, location found, condition" },
        ],
      },
    },
    {
      id: "s3",
      title: "Section 3 — Evidence Preservation",
      checklist: {
        id: "evidence_pres",
        label: "Evidence Preservation",
        items: [
          { id: "ppe", label: "Evidence collected using appropriate PPE to prevent contamination" },
          { id: "packaging", label: "Packaging standards applied by evidence type (biological / physical / digital / trace)" },
          { id: "separate", label: "Each item packaged separately" },
          { id: "sealed", label: "Items sealed, labeled with case number, date, time, collecting officer" },
          { id: "biological", label: "Biological evidence refrigerated or humidity-controlled if required" },
          { id: "digital", label: "Digital devices not activated, charged, or connected to networks" },
        ],
      },
    },
    {
      id: "s4",
      title: "Section 4 — Chain of Custody Documentation",
      checklist: {
        id: "chain_custody",
        label: "Chain of Custody Documentation",
        items: [
          { id: "coc_form", label: "Chain of custody form initiated for each evidence item" },
          { id: "transfers", label: "Every transfer documented: who gave it, who received it, date, time, purpose" },
          { id: "no_undoc", label: "No undocumented transfers permitted" },
          { id: "property_room", label: "Evidence submitted to property room with documentation complete" },
          { id: "receipt", label: "Receipt from property officer obtained and attached to case file" },
          { id: "break_doc", label: "Any chain of custody break documented immediately with supervisor notification" },
        ],
      },
    },
    {
      id: "s5",
      title: "Section 5 — Scene Release Protocol",
      checklist: {
        id: "scene_release",
        label: "Scene Release Protocol",
        items: [
          { id: "auth_release", label: "Lead investigator or supervisor authorizes scene release in writing" },
          { id: "release_doc", label: "Scene release documentation completed with date, time, authorizing officer" },
          { id: "owner_notify", label: "Property owner or responsible party notified of release in writing" },
          { id: "restoration", label: "Scene restoration requirements communicated" },
          { id: "hold_doc", label: "Any hold on portions of scene documented" },
        ],
      },
    },
  ],
};

export const T15: TemplateDefinition = {
  id: "t15",
  module: 4,
  number: 15,
  name: "Professional Report Writing System and Template Library",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "A prosecutor makes charging decisions based on your report. A defense attorney looks for inconsistencies in your report. This system produces reports that hold up to both.",
  architectureContext:
    "This template serves the Value-Based Credentialing pillar. It provides a library of report frameworks with self-review checklists — ensuring your reports withstand prosecutorial and defense scrutiny.",
  sections: [
    {
      id: "tab1",
      title: "Tab 1 — Incident Report Framework",
      variant: "tabs",
      fields: [
        { id: "case_num", type: "text", label: "Case Number", placeholder: "Case #" },
        { id: "date", type: "date", label: "Date" },
        { id: "time", type: "text", label: "Time", placeholder: "HH:MM" },
        { id: "location", type: "text", label: "Location", placeholder: "Incident location..." },
        { id: "officer_name", type: "text", label: "Officer Name", prefillFrom: "name" },
        { id: "badge_num", type: "text", label: "Badge Number", prefillFrom: "badge" },
      ],
      checklist: {
        id: "incident_req",
        label: "Required Elements",
        items: [
          { id: "dtl", label: "Date, time, location of incident" },
          { id: "how_aware", label: "How officer became aware (dispatch / self-initiated / citizen contact)" },
          { id: "victims", label: "Victim(s) — full identification information" },
          { id: "suspects", label: "Suspect(s) — full identification or description" },
          { id: "witnesses", label: "Witness(es) — identification and location at time of incident" },
          { id: "observations", label: "Officer observations — first person, sensory (what I saw, heard, smelled, felt)" },
          { id: "statements", label: "Statements received — who said what, verbatim" },
          { id: "evidence_collected", label: "Evidence collected — reference evidence log" },
          { id: "actions", label: "Actions taken — chronological" },
          { id: "charges", label: "Charges if applicable" },
          { id: "disposition", label: "Disposition" },
        ],
      },
      repeatable: undefined,
    },
    {
      id: "tab1_review",
      title: "Tab 1 — Self-Review Checklist",
      checklist: {
        id: "incident_review",
        label: "Self-Review",
        items: [
          { id: "r_www", label: "Does this report answer who, what, when, where, how, and why?" },
          { id: "r_source", label: "Does every fact have a documented source?" },
          { id: "r_first", label: "Is the narrative written in first person, past tense, objective language?" },
          { id: "r_elements", label: "Are all legal elements of the charge documented?" },
          { id: "r_chron", label: "Is the chronological sequence accurate?" },
          { id: "r_names", label: "Are all names, badge numbers, case numbers correct?" },
        ],
      },
    },
    {
      id: "tab2",
      title: "Tab 2 — Arrest Report Framework",
      variant: "tabs",
      fields: [
        { id: "ar_case", type: "text", label: "Case Number", placeholder: "Case #" },
        { id: "ar_date", type: "date", label: "Date" },
        { id: "ar_time", type: "text", label: "Time", placeholder: "HH:MM" },
        { id: "ar_location", type: "text", label: "Location", placeholder: "Arrest location..." },
        { id: "ar_officer", type: "text", label: "Officer Name", prefillFrom: "name" },
        { id: "ar_badge", type: "text", label: "Badge Number", prefillFrom: "badge" },
      ],
      checklist: {
        id: "arrest_req",
        label: "Required Elements",
        items: [
          { id: "pc_statement", label: "Probable cause statement — specific facts establishing each element" },
          { id: "miranda_doc", label: "Miranda advisement documented" },
          { id: "sita_doc", label: "Search incident to arrest documented with legal basis" },
          { id: "property_inv", label: "Property inventory completed" },
          { id: "medical", label: "Medical screening documented" },
          { id: "booking", label: "Booking information" },
        ],
      },
    },
    {
      id: "tab2_review",
      title: "Tab 2 — Self-Review Checklist",
      checklist: {
        id: "arrest_review",
        label: "Self-Review",
        items: [
          { id: "ar_www", label: "Does this report answer who, what, when, where, how, and why?" },
          { id: "ar_source", label: "Does every fact have a documented source?" },
          { id: "ar_first", label: "Is the narrative written in first person, past tense, objective language?" },
          { id: "ar_elements", label: "Does the probable cause statement establish every element of the charge?" },
          { id: "ar_chron", label: "Is the chronological sequence accurate?" },
          { id: "ar_names", label: "Are all names, badge numbers, case numbers correct?" },
        ],
      },
    },
    {
      id: "tab3",
      title: "Tab 3 — Use-of-Force Report Framework",
      variant: "tabs",
      checklist: {
        id: "uof_req",
        label: "Required Elements",
        items: [
          { id: "uof_dtl", label: "Date, time, location" },
          { id: "uof_reason", label: "Reason for contact / call type" },
          { id: "uof_behavior", label: "Subject behavior — specific observable behaviors that preceded force" },
          { id: "uof_level", label: "Threat level applied from Decision Matrix (Template 7) — document the level and why" },
          { id: "uof_force", label: "Force used — type, duration, technique" },
          { id: "uof_standard", label: "Constitutional standard applied — Graham v. Connor objective reasonableness factors" },
          { id: "uof_deesc", label: "De-escalation attempted — what was tried and why it was insufficient" },
          { id: "uof_outcome", label: "Outcome — subject's condition after force" },
          { id: "uof_medical", label: "Medical evaluation offered/provided" },
          { id: "uof_super", label: "Supervisor notified — name, time, method" },
          { id: "uof_bodycam", label: "Body camera activation status" },
          { id: "uof_witnesses", label: "Witness officer names and badge numbers" },
        ],
      },
      fields: [
        { id: "uof_review_text", type: "textarea", label: "Self-Review: Does every force decision reference a specific subject behavior? Does the narrative establish objective reasonableness?", placeholder: "Your self-review notes..." },
      ],
    },
    {
      id: "tab4",
      title: "Tab 4 — Search and Seizure Report Framework",
      variant: "tabs",
      checklist: {
        id: "search_req",
        label: "Required Elements",
        items: [
          { id: "ss_dtl", label: "Date, time, location" },
          { id: "ss_type", label: "Type of search documented with its legal basis" },
          { id: "ss_items_searched", label: "Items searched (locations and containers)" },
          { id: "ss_items_seized", label: "Items seized — description, location found, evidence log number" },
          { id: "ss_analysis", label: "Constitutional analysis documented (reference Template 5)" },
        ],
      },
      fields: [
        { id: "ss_review_text", type: "textarea", label: "Self-Review: Is the legal basis for the search documented with specific facts? Are all seized items in the evidence log?", placeholder: "Your self-review notes..." },
      ],
    },
    {
      id: "tab5",
      title: "Tab 5 — Witness Statement Framework",
      variant: "tabs",
      checklist: {
        id: "witness_req",
        label: "Required Elements",
        items: [
          { id: "ws_id", label: "Witness identification (full name, DOB, address, phone, relationship to incident)" },
          { id: "ws_method", label: "Statement taken: in person / phone / written / recorded" },
          { id: "ws_time_loc", label: "Time and location of statement" },
          { id: "ws_officer", label: "Officer who took statement" },
          { id: "ws_verbatim", label: "Verbatim statement (or summary if not recorded verbatim)" },
          { id: "ws_contradictions", label: "Contradictions with other evidence or statements noted (documented separately and factually)" },
          { id: "ws_signed", label: "Witness signed the statement (yes/no + documentation method)" },
          { id: "ws_contact", label: "Witness provided additional contact information" },
        ],
      },
    },
  ],
};

export const T16: TemplateDefinition = {
  id: "t16",
  module: 4,
  number: 16,
  name: "Radio Communications Protocol and Evidence Chain Documentation System",
  stage: "STAGE 2 — COMPETENCY",
  pillars: "Value-Based Credentialing + Tiered Access",
  whyExists:
    "Radio communications become part of the evidentiary record. A broken chain of custody documented on radio cannot be undone. These two linked tools prevent both failures.",
  architectureContext:
    "This template serves the Tiered Access pillar. It links radio communications protocol with evidence chain documentation — ensuring both your transmissions and your evidence transfers are defensible.",
  sections: [
    {
      id: "part1",
      title: "PART 1 — Radio Communications Protocol",
      description: "Section 1A — Standard Transmission Format",
      fields: [
        { id: "trans_format", type: "textarea", label: "The transmission format I use for all field communications", placeholder: "Unit ID, location, traffic type, message" },
        { id: "phonetic", type: "textarea", label: "Phonetic alphabet proficiency — my practice reference", placeholder: "Your practice approach for the NATO phonetic alphabet..." },
        { id: "radio_codes", type: "textarea", label: "My department's radio codes and 10-codes reference", placeholder: "Your department-specific codes..." },
      ],
    },
    {
      id: "s1b",
      title: "Section 1B — Officer Safety Communications",
      fields: [
        { id: "status_check", type: "text", label: "My status check protocol — frequency and method", placeholder: "e.g., Every 15 minutes via radio" },
        { id: "location_update", type: "textarea", label: "My location update protocol — when and how I update dispatch", placeholder: "Your location update protocol..." },
        { id: "crisis_lang", type: "textarea", label: "Clear language protocols for crisis transmissions — the language I use for officer down, shots fired, pursuit initiation", placeholder: "Your crisis language protocols..." },
        { id: "emerg_activation", type: "textarea", label: "My emergency activation procedure", placeholder: "Your emergency activation procedure..." },
      ],
    },
    {
      id: "s1c",
      title: "Section 1C — Radio as Evidence",
      fields: [
        { id: "radio_defensible", type: "textarea", label: "How I ensure my radio communications are accurate and defensible", placeholder: "Your approach..." },
        { id: "radio_doc", type: "textarea", label: "What radio transmissions I document in my report", placeholder: "What you document..." },
        { id: "retention", type: "text", label: "My department's radio recording retention policy", placeholder: "e.g., 90 days" },
      ],
    },
    {
      id: "part2",
      title: "PART 2 — Evidence Chain Documentation System",
      description: "Section 2A — Transfer Point Documentation",
      repeatable: {
        id: "transfer_log",
        label: "Transfer Entry",
        maxRows: 20,
        initialRows: 1,
        addLabel: "+ Add Transfer",
        fields: [
          { id: "item_num", type: "text", label: "Item Number (references evidence log)", placeholder: "Item #" },
          { id: "from", type: "text", label: "Transferred From", placeholder: "From..." },
          { id: "to", type: "text", label: "Transferred To", placeholder: "To..." },
          { id: "datetime", type: "datetime-local", label: "Date and Time of Transfer" },
          { id: "purpose", type: "text", label: "Purpose of Transfer", placeholder: "Purpose..." },
          { id: "method", type: "text", label: "Transfer Method", placeholder: "Method..." },
          { id: "sig_ref", type: "text", label: "Signature/Documentation Reference", placeholder: "Reference..." },
        ],
      },
    },
    {
      id: "s2b",
      title: "Section 2B — Digital Evidence Additional Requirements",
      fields: [
        { id: "auth_digital", type: "textarea", label: "Authorization required before accessing digital devices", placeholder: "Your authorization protocol..." },
        { id: "imaging", type: "textarea", label: "Imaging and extraction protocol my department uses", placeholder: "Your imaging protocol..." },
        { id: "digital_doc", type: "textarea", label: "How I document digital evidence extraction in the chain of custody", placeholder: "Your documentation approach..." },
        { id: "network_iso", type: "textarea", label: "Network isolation requirement for digital devices", placeholder: "Your isolation requirements..." },
      ],
    },
    {
      id: "s2c",
      title: "Section 2C — Chain of Custody Break Protocol",
      fields: [
        { id: "break_doc", type: "textarea", label: "How I document a chain of custody break immediately upon discovery", placeholder: "Your break documentation protocol..." },
        { id: "break_super", type: "text", label: "Supervisor notification requirement", placeholder: "e.g., Notify Shift Sergeant immediately" },
        { id: "break_report", type: "textarea", label: "How I report a chain of custody break in the case file", placeholder: "Your reporting approach..." },
      ],
    },
  ],
};
